import React, { useState } from "react";
import {
  Badge,
  Button,
  Code,
  Flex,
  Grid,
  Group,
  Modal,
  Pagination,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";

import MainHeading from "components/MainHeading";
import NotFound from "components/NotFound";
import { useUser } from "hooks/useUser";
import { useHasPermission } from "api/permission/useHasPermission";
import {
  AuditLogEntry,
  useAuditFilters,
  useAuditLogs,
} from "api/logs/getAuditLogs";

const actionColor = (action: string) => {
  if (action.startsWith("create")) return "green";
  if (action.startsWith("delete")) return "red";
  if (action.startsWith("update")) return "blue";
  return "gray";
};

const actorLabel = (entry: AuditLogEntry) => {
  const p = entry.performedBy;
  if (!p) return "System";
  if (typeof p === "string") return p;
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ");
  return name || p.email || p.role || "Unknown";
};

export function AuditLog() {
  const { user } = useUser();
  // Access is driven by the assignable `view_audit_logs` permission.
  // Superadmin always has access (mirrors the backend gate).
  const { has: canViewAudit, isLoading: permLoading } = useHasPermission(
    "view_audit_logs"
  );
  const isSuperAdmin = (user?.role as string | undefined) === "superadmin";
  const allowed = isSuperAdmin || canViewAudit;

  const [page, setPage] = useState(1);
  const [email, setEmail] = useState("");
  const [emailFilter, setEmailFilter] = useState<string | undefined>();
  const [targetModel, setTargetModel] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  const { data: filterOptions } = useAuditFilters();
  const { data, isLoading, isError } = useAuditLogs({
    page,
    limit: 20,
    email: emailFilter,
    targetModel: targetModel || undefined,
    action: action || undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  });

  if (permLoading && !isSuperAdmin) return <Text color="dimmed">Loading…</Text>;
  if (!allowed) return <NotFound />;

  const applyEmail = () => {
    setPage(1);
    setEmailFilter(email.trim() || undefined);
  };

  const clearFilters = () => {
    setEmail("");
    setEmailFilter(undefined);
    setTargetModel(null);
    setAction(null);
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const logs = data?.logs ?? [];
  const pagination = data?.pagination;

  const renderBody = () => {
    if (isError) return <Text color="red">Failed to load audit logs.</Text>;
    if (isLoading) return <Text color="dimmed">Loading…</Text>;
    if (logs.length === 0) return <Text color="dimmed">No audit records match these filters.</Text>;
    return (
      <ScrollArea>
        <Table striped highlightOnHover fontSize="xs" miw={900}>
          <thead>
            <tr>
              <th>When</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Model</th>
              <th>Changed fields</th>
              <th>IP</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {logs.map((entry) => (
              <tr key={entry._id}>
                <td>{new Date(entry.createdAt).toLocaleString()}</td>
                <td>{actorLabel(entry)}</td>
                <td>
                  <Badge color={actionColor(entry.action)} variant="light">
                    {entry.action}
                  </Badge>
                </td>
                <td>{entry.targetModel ?? entry.module}</td>
                <td>
                  <Text lineClamp={1} maw={220}>
                    {entry.changedFields?.join(", ") || "—"}
                  </Text>
                </td>
                <td>{entry.ip ?? "—"}</td>
                <td>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => setSelected(entry)}
                  >
                    Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScrollArea>
    );
  };

  return (
    <>
      <MainHeading title="Audit Log" />

      <Grid gutter="sm" mb="md" align="flex-end">
        <Grid.Col span={12} md={3}>
          <TextInput
            label="User email"
            placeholder="Filter by actor email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && applyEmail()}
          />
        </Grid.Col>
        <Grid.Col span={6} md={2}>
          <Select
            label="Model"
            placeholder="All"
            clearable
            searchable
            data={(filterOptions?.targetModels ?? []).map((m) => ({
              value: m,
              label: m,
            }))}
            value={targetModel}
            onChange={(v) => {
              setPage(1);
              setTargetModel(v);
            }}
          />
        </Grid.Col>
        <Grid.Col span={6} md={2}>
          <Select
            label="Action"
            placeholder="All"
            clearable
            searchable
            data={(filterOptions?.actions ?? []).map((a) => ({
              value: a,
              label: a,
            }))}
            value={action}
            onChange={(v) => {
              setPage(1);
              setAction(v);
            }}
          />
        </Grid.Col>
        <Grid.Col span={6} md={2}>
          <TextInput
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => {
              setPage(1);
              setFromDate(e.currentTarget.value);
            }}
          />
        </Grid.Col>
        <Grid.Col span={6} md={2}>
          <TextInput
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => {
              setPage(1);
              setToDate(e.currentTarget.value);
            }}
          />
        </Grid.Col>
        <Grid.Col span={12} md={1}>
          <Group spacing="xs" noWrap>
            <Button size="xs" onClick={applyEmail}>
              Search
            </Button>
            <Button size="xs" variant="light" onClick={clearFilters}>
              Clear
            </Button>
          </Group>
        </Grid.Col>
      </Grid>

      {renderBody()}

      {pagination && pagination.totalPages > 1 && (
        <Flex justify="center" mt="md">
          <Pagination
            page={page}
            onChange={setPage}
            total={pagination.totalPages}
            withEdges
          />
        </Flex>
      )}

      <Modal
        opened={!!selected}
        onClose={() => setSelected(null)}
        title={
          selected
            ? `${selected.action} · ${selected.targetModel ?? selected.module}`
            : ""
        }
        size="xl"
      >
        {selected && (
          <>
            <Text size="sm" mb="xs">
              <b>Actor:</b> {actorLabel(selected)} &nbsp;|&nbsp; <b>When:</b>{" "}
              {new Date(selected.createdAt).toLocaleString()}
            </Text>
            <Text size="sm" mb="xs">
              <b>Target id:</b> {String(selected.targetId ?? "—")} &nbsp;|&nbsp;{" "}
              <b>IP:</b> {selected.ip ?? "—"}
            </Text>
            {selected.userAgent && (
              <Text size="xs" color="dimmed" mb="sm">
                {selected.userAgent}
              </Text>
            )}
            <Grid>
              <Grid.Col span={6}>
                <Title order={6} mb={4}>
                  Old value
                </Title>
                <ScrollArea h={360}>
                  <Code block>
                    {selected.oldValue
                      ? JSON.stringify(selected.oldValue, null, 2)
                      : "—"}
                  </Code>
                </ScrollArea>
              </Grid.Col>
              <Grid.Col span={6}>
                <Title order={6} mb={4}>
                  New value
                </Title>
                <ScrollArea h={360}>
                  <Code block>
                    {selected.newValue
                      ? JSON.stringify(selected.newValue, null, 2)
                      : "—"}
                  </Code>
                </ScrollArea>
              </Grid.Col>
            </Grid>
            {selected.data ? (
              <>
                <Title order={6} mt="sm" mb={4}>
                  Data
                </Title>
                <Code block>{JSON.stringify(selected.data, null, 2)}</Code>
              </>
            ) : null}
          </>
        )}
      </Modal>
    </>
  );
}
